import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '../api/client'

interface PromoCode {
  id: number
  code: string
  publication_id: number | null
  stripe_promotion_code_id: string | null
  description: string | null
  offer_kind: string | null
  percent_off: string | null
  amount_off_cents: number | null
  duration: string
  duration_in_months: number | null
  expires_at: string | null
  active: boolean
  created_at: string | null
  use_count: number
  // Serialised from a Decimal, so it arrives as a string.
  use_amount: string
}

interface PromoCodeList {
  items: PromoCode[]
}

// Matches the autoincrement ids seeded in aamcirc/db/seed.py. There is no
// endpoint that lists publications, so — same as Templates.tsx and its
// siblings — this maps ids to display names locally. Duplicated on purpose;
// hoisting it into a shared module is explicitly out of scope here.
const PUB_NAME_BY_ID: Record<number, string> = {
  1: 'RC Car Action',
  2: 'Model Airplane News',
  3: 'Flight Journal',
  4: 'Die Cast X',
}

function discountLabel(c: PromoCode): string {
  if (c.offer_kind === 'bogo') return 'Buy one get one'
  if (c.percent_off) return `${parseFloat(c.percent_off)}% off`
  if (c.amount_off_cents) return `$${(c.amount_off_cents / 100).toFixed(2)} off`
  return '—'
}

function durationLabel(c: PromoCode): string {
  if (c.duration === 'forever') return 'Forever'
  if (c.duration === 'repeating') return `${c.duration_in_months} months`
  return 'First payment'
}

// What buyers actually paid under the code — already discounted, and
// not net of refunds (a refund row carries no code, same as the count).
function revenueLabel(c: PromoCode): string {
  return `$${parseFloat(c.use_amount).toFixed(2)}`
}

function titleLabel(c: PromoCode): string {
  if (c.publication_id === null) return 'All titles'
  return PUB_NAME_BY_ID[c.publication_id] ?? String(c.publication_id)
}

// expires_at is a date-only value stored as UTC midnight. Rendering it in
// the browser's local zone reads a day early in every US timezone, so the
// date portion is pulled out and formatted in UTC.
function expiresLabel(c: PromoCode): string {
  if (!c.expires_at) return '—'
  return new Date(c.expires_at).toLocaleDateString(undefined, { timeZone: 'UTC' })
}

// ApiError.message is a generic "API error {status}" — the real reason
// FastAPI sent (a 409 duplicate-code message, a 422 validation error) lives
// in .detail. Matches the pattern in TemplateEditor.tsx / CampaignEditor.tsx
// / MarketingLanding.tsx / Trailers.tsx, extended to also unwrap the array
// shape FastAPI sends for a 422 request-validation error.
function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    if (typeof err.detail === 'object' && err.detail !== null) {
      const d = (err.detail as Record<string, unknown>).detail
      if (typeof d === 'string') return d
      if (Array.isArray(d)) {
        const first = d[0] as { msg?: string } | undefined
        if (first?.msg) return first.msg
      }
    }
    return err.message
  }
  return err instanceof Error ? err.message : 'Request failed'
}

type DiscountKind = 'percent' | 'amount'
type OfferKind = 'discount' | 'bogo'

export function PromoCodesPage() {
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [offerKind, setOfferKind] = useState<OfferKind>('discount')
  const [discountKind, setDiscountKind] = useState<DiscountKind>('percent')
  const [percentOff, setPercentOff] = useState('')
  const [amountOff, setAmountOff] = useState('')
  const [publicationId, setPublicationId] = useState('')
  const [duration, setDuration] = useState('once')
  const [durationInMonths, setDurationInMonths] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [error, setError] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ['promo-codes'],
    queryFn: () => api.get<PromoCodeList>('/api/promo-codes'),
  })

  const parsedAmountOff = parseFloat(amountOff)
  const parsedMonths = Number(durationInMonths)
  const validPercent = discountKind === 'percent' && percentOff !== ''
  const validAmount =
    discountKind === 'amount' && amountOff !== '' && !Number.isNaN(parsedAmountOff) && parsedAmountOff > 0
  const validMonths =
    offerKind === 'bogo' ||
    duration !== 'repeating' ||
    (durationInMonths !== '' && Number.isInteger(parsedMonths) && parsedMonths > 0)
  // A bogo code carries no discount, so the percent/amount check that
  // gates an ordinary code doesn't apply to it. Its "Lasts" control is
  // hidden too, so validMonths is unconditionally satisfied above.
  const canSubmit =
    code !== '' && (offerKind === 'bogo' || validPercent || validAmount) && validMonths

  const create = useMutation({
    mutationFn: () => {
      // A bogo grants a membership once — "repeating" or "forever" is
      // meaningless for a code with no discount to carry across cycles.
      // The Lasts/Months controls are hidden for bogo, but their state
      // can still be stale from before the operator switched Offer, so
      // this branches on offerKind rather than trusting duration/
      // durationInMonths to already be reset.
      const effectiveDuration = offerKind === 'bogo' ? 'once' : duration
      const body: Record<string, unknown> = {
        code,
        description: description || null,
        publication_id: publicationId ? Number(publicationId) : null,
        duration: effectiveDuration,
        duration_in_months:
          effectiveDuration === 'repeating' ? Number(durationInMonths) : null,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
      }
      if (offerKind === 'bogo') {
        // A bogo grants a second membership rather than discounting a
        // price — percent_off and amount_off_cents must stay absent, or
        // the API 422s it.
        body.offer_kind = 'bogo'
      } else if (discountKind === 'percent') {
        // Exactly one of percent_off / amount_off_cents — the API 422s if
        // both or neither are present, so the unselected kind's key is
        // omitted entirely rather than sent as null alongside the other's
        // value.
        body.percent_off = percentOff
      } else {
        body.amount_off_cents = Math.round(parsedAmountOff * 100)
      }
      return api.post<PromoCode>('/api/promo-codes', body)
    },
    onSuccess: () => {
      setCode('')
      setDescription('')
      setOfferKind('discount')
      setPercentOff('')
      setAmountOff('')
      setPublicationId('')
      // duration/durationInMonths aren't cosmetic — leaving them set means
      // the next code silently defaults to "repeating / 3 months" again,
      // a money-affecting default an operator creating a fresh code is
      // unlikely to notice and re-check.
      setDuration('once')
      setDurationInMonths('')
      setExpiresAt('')
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['promo-codes'] })
    },
    onError: (e: unknown) => setError(describeError(e)),
  })

  const deactivate = useMutation({
    mutationFn: (id: number) => api.post<PromoCode>(`/api/promo-codes/${id}/deactivate`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['promo-codes'] }),
  })

  const items = list.data?.items ?? []

  return (
    <div className="p-8 space-y-6 max-w-[1200px]">
      <header className="border-b border-ink-500 pb-6">
        <div className="label mb-1">Membership · pricing</div>
        <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
          Promo Codes
        </h1>
        <p className="mt-2 text-sm text-paper-dim max-w-[62ch]">
          Discounts on paid memberships. Link a campaign to{' '}
          <code>?promo=CODE</code> and the price shows discounted on the
          pricing page and is applied at checkout.
        </p>
      </header>

      {deactivate.isError && (
        <p className="text-sm text-red-400">
          Couldn't retire that code: {describeError(deactivate.error)}
        </p>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="label border-b border-ink-500">
            <th className="text-left py-2">Code</th>
            <th className="text-left">Discount</th>
            <th className="text-left">Lasts</th>
            <th className="text-left">Title</th>
            <th className="text-left">Expires</th>
            {/* Paid orders through our own checkout that carried the code. */}
            <th className="text-left">Used</th>
            <th className="text-left">Revenue</th>
            <th className="text-left">Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const retiring = deactivate.isPending && deactivate.variables === c.id
            return (
              <tr key={c.id} className="border-b border-ink-500/50">
                <td className="py-2 text-paper font-mono">{c.code}</td>
                <td className="text-paper-dim">{discountLabel(c)}</td>
                <td className="text-paper-dim">{durationLabel(c)}</td>
                <td className="text-paper-dim">{titleLabel(c)}</td>
                <td className="text-paper-dim">{expiresLabel(c)}</td>
                <td className="text-paper-dim tabular-nums">{c.use_count}</td>
                <td className="text-paper-dim tabular-nums">{revenueLabel(c)}</td>
                <td className={c.active ? 'text-amber-glow' : 'text-paper-dim'}>
                  {c.active ? 'Live' : 'Retired'}
                </td>
                <td className="text-right">
                  {c.active && (
                    <button
                      type="button"
                      className="text-xs text-paper-dim underline disabled:opacity-40"
                      disabled={retiring}
                      onClick={() => deactivate.mutate(c.id)}
                    >
                      {retiring ? 'Retiring…' : 'Retire'}
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
          {items.length === 0 && !list.isLoading && (
            <tr>
              <td colSpan={9} className="py-6 text-paper-dim">
                No promo codes yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <section className="border border-ink-500 p-5 space-y-4">
        <div className="label">New code</div>
        <div className="grid grid-cols-2 gap-4">
          <label className="text-sm text-paper-dim">
            Code
            <input
              className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="SAVE20"
            />
          </label>
          <label className="text-sm text-paper-dim">
            Description
            <input
              className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Autumn campaign"
            />
          </label>
          <label className="text-sm text-paper-dim">
            Offer
            <select
              className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
              value={offerKind}
              onChange={(e) => setOfferKind(e.target.value as OfferKind)}
            >
              <option value="discount">Discount</option>
              <option value="bogo">Buy one get one</option>
            </select>
          </label>
          {offerKind === 'discount' && (
            <label className="text-sm text-paper-dim">
              Discount type
              <select
                className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
                value={discountKind}
                onChange={(e) => setDiscountKind(e.target.value as DiscountKind)}
              >
                <option value="percent">Percent off</option>
                <option value="amount">Amount off ($)</option>
              </select>
            </label>
          )}
          {offerKind === 'discount' && (discountKind === 'percent' ? (
            <label className="text-sm text-paper-dim">
              Percent off
              <input
                className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
                value={percentOff}
                onChange={(e) => setPercentOff(e.target.value)}
                placeholder="20"
              />
            </label>
          ) : (
            <label className="text-sm text-paper-dim">
              Amount off ($)
              <input
                className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
                value={amountOff}
                onChange={(e) => setAmountOff(e.target.value)}
                placeholder="5.00"
              />
            </label>
          ))}
          <label className="text-sm text-paper-dim">
            Title
            <select
              className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
              value={publicationId}
              onChange={(e) => setPublicationId(e.target.value)}
            >
              <option value="">All titles</option>
              {Object.entries(PUB_NAME_BY_ID).map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </label>
          {offerKind === 'discount' && (
            <label className="text-sm text-paper-dim">
              Lasts
              <select
                className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
              >
                <option value="once">First payment only</option>
                <option value="repeating">A set number of months</option>
                <option value="forever">Forever</option>
              </select>
            </label>
          )}
          {offerKind === 'discount' && duration === 'repeating' && (
            <label className="text-sm text-paper-dim">
              Months
              <input
                className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
                value={durationInMonths}
                onChange={(e) => setDurationInMonths(e.target.value)}
                placeholder="3"
              />
            </label>
          )}
          <label className="text-sm text-paper-dim">
            Expires (optional)
            <input
              type="date"
              className="mt-1 w-full bg-ink-900 border border-ink-500 px-3 py-2 text-paper"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </label>
        </div>

        {error && <p className="text-sm text-red-400">{error}</p>}

        <button
          type="button"
          className="border border-amber-glow px-4 py-2 text-sm text-amber-glow disabled:opacity-40"
          disabled={!canSubmit || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? 'Creating…' : 'Create code'}
        </button>
        {/* No edit: a Stripe coupon's percent and duration are immutable, so
            retiring and creating a new code is the only honest change. */}
        <p className="text-xs text-paper-dim">
          Codes can't be edited once created — retire one and make a new one.
        </p>
      </section>
    </div>
  )
}
